import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import apiClient from '@/utils/api';
import { Card } from '@/components/ui/card';

const fmtCve = (v) => `${Number(v || 0).toLocaleString('pt-PT')} CVE`;
const MEDAL = { 1: 'bg-amber-400 text-amber-950', 2: 'bg-slate-300 text-slate-900', 3: 'bg-orange-300 text-orange-950' };

export const InfluencerLeaderboard = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get('/admin/influencers/leaderboard', { params: { period, limit: 10 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], totals: {} }));
  }, [period, refreshKey]);

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <Card className="p-5 mb-6" data-testid="influencer-leaderboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" />{t('adminInfluencers.lbTitle')}</h2>
          <p className="text-sm text-slate-600 mt-0.5">{t('adminInfluencers.lbHint')}</p>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-white" role="tablist">
          {['month', 'all'].map((p) => (
            <button key={p} type="button" role="tab" aria-selected={period === p} onClick={() => setPeriod(p)} data-testid={`lb-period-${p}`}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${period === p ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {p === 'month' ? t('adminInfluencers.lbMonth') : t('adminInfluencers.lbAll')}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 || items.every((r) => r.referrals === 0 && r.payout_total_cve === 0) ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="lb-empty">{t('adminInfluencers.lbEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lb-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="py-2 pr-2 w-10">{t('adminInfluencers.lbRank')}</th>
                <th className="py-2 pr-2">{t('adminInfluencers.lbName')}</th>
                <th className="py-2 pr-2 text-right">{t('adminInfluencers.lbEmployers')}</th>
                <th className="py-2 pr-2 text-right">{t('adminInfluencers.lbCandidates')}</th>
                <th className="py-2 pr-2 text-right">{t('adminInfluencers.lbBonus')}</th>
                <th className="py-2 text-right">{t('adminInfluencers.lbPayout')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`lb-row-${r.code}`}>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${MEDAL[r.rank] || 'bg-slate-100 text-slate-600'}`}>{r.rank}</span>
                  </td>
                  <td className="py-2 pr-2"><span className="font-semibold text-slate-900">{r.name}</span> <span className="text-xs text-slate-500 font-mono">?ref={r.code}</span></td>
                  <td className="py-2 pr-2 text-right font-semibold" data-testid={`lb-employers-${r.code}`}>{r.employers}</td>
                  <td className="py-2 pr-2 text-right font-semibold">{r.candidates}</td>
                  <td className="py-2 pr-2 text-right text-slate-700">{fmtCve(r.bonus_period_cve)}</td>
                  <td className="py-2 text-right text-emerald-700 font-semibold">{fmtCve(r.payout_total_cve)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-xs text-slate-600 border-t">
                <td className="py-2 pr-2" colSpan={2}>{t('adminInfluencers.lbTotals')}</td>
                <td className="py-2 pr-2 text-right" colSpan={2}>{totals.referrals ?? 0}</td>
                <td className="py-2 pr-2 text-right">{fmtCve(totals.bonus_period_cve)}</td>
                <td className="py-2 text-right">{fmtCve(totals.payout_total_cve)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
};
